import React, { useState, useRef, useEffect } from "react";
import {
  QrCodeIcon,
  TruckIcon,
  CheckCircleIcon,
  TrashIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { request } from "config/request";
import UseToast from "utils/AfToast";
import Swal from "sweetalert2";
import { soundFX } from "../soundEffects";

interface StagedPair {
  id: string;
  awbNo: string;
  qrCode: string;
  time: string;
}

/**
 * Format specifications:
 * AWB format: First alphabet and rest digits (e.g. A12345678, D987654)
 * Box QR format: All numeric (123123), BX-123123, 123123, or AHD123123
 */
export const isAwbFormat = (val: string): boolean => {
  const trimmed = val.trim();
  // Exactly 1 alphabet letter at the start, followed by digits only
  return /^[a-zA-Z]\d+$/.test(trimmed);
};

export const isBoxQrFormat = (val: string): boolean => {
  const trimmed = val.trim();
  if (!trimmed) return false;
  // An AWB has 1 alphabet + digits; if it matches AWB, it's NOT a Box QR
  if (isAwbFormat(trimmed)) return false;

  // 1) All numeric (e.g. 123123)
  if (/^\d+$/.test(trimmed)) return true;

  // 2) Starts with 2 or more letters followed by optional delimiter and digits/alphanumeric (e.g. BX-123123, AHD123123, BX123123)
  if (/^[a-zA-Z]{2,}[-_]?[a-zA-Z0-9]+$/i.test(trimmed)) return true;

  // 3) Single letter with hyphen/underscore separator (e.g. B-123123)
  if (/^[a-zA-Z][-_][a-zA-Z0-9]+$/i.test(trimmed)) return true;

  return false;
};

export const AwbPackingTab: React.FC = () => {
  const [scanInput, setScanInput] = useState("");
  const [pendingAwb, setPendingAwb] = useState<string | null>(null);
  const [stagedPairs, setStagedPairs] = useState<StagedPair[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastResults, setLastResults] = useState<any[]>([]);

  const [lastScannedBox, setLastScannedBox] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleScanSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = scanInput.trim();
    if (!raw) return;

    setScanError(null);

    const isAwb = isAwbFormat(raw);
    const isBox = isBoxQrFormat(raw);

    if (isAwb) {
      // Scanned an AWB (e.g. A12345678)
      const scannedAwb = raw.toUpperCase();

      if (pendingAwb !== null) {
        // Scanned 2 AWBs in a row -> Missed Box QR!
        soundFX.playError();
        const err = `Missed Box QR! You scanned 2 AWBs in a row (scanned #${scannedAwb} while waiting for Box QR on #${pendingAwb}). Please scan Box QR for AWB #${pendingAwb} or cancel it.`;
        setScanError(err);
        UseToast(err, "error");
        setScanInput("");
        return;
      }

      // Valid: Record pending AWB
      setPendingAwb(scannedAwb);
      soundFX.playSuccess();
      setScanInput("");
      inputRef.current?.focus();
    } else if (isBox) {
      // Scanned a Box QR (e.g. 123123, BX-123123, AHD123123)
      const scannedBox = raw.toUpperCase();

      if (pendingAwb === null) {
        // Scanned Box QR before AWB -> Missed AWB!
        soundFX.playError();
        const err = lastScannedBox
          ? `Missed AWB Number! You scanned 2 Box QRs in a row (scanned '${scannedBox}' right after '${lastScannedBox}'). Please scan the next AWB (e.g. A12345678) first.`
          : `Missed AWB Number! Please scan an AWB first (format: 1 letter + digits, e.g. A12345678) before Box QR '${scannedBox}'.`;
        setScanError(err);
        UseToast(err, "error");
        setScanInput("");
        return;
      }

      // Check duplicate box QR in current staging batch
      if (stagedPairs.some((p) => p.qrCode === scannedBox)) {
        soundFX.playError();
        const err = `Box '${scannedBox}' is already added in this batch!`;
        setScanError(err);
        UseToast(err, "warning");
        setScanInput("");
        return;
      }

      // Valid pair completed!
      const newPair: StagedPair = {
        id: `${Date.now()}-${Math.random()}`,
        awbNo: pendingAwb,
        qrCode: scannedBox,
        time: new Date().toLocaleTimeString(),
      };

      soundFX.playSuccess();
      setStagedPairs((prev) => [newPair, ...prev]);
      setLastScannedBox(scannedBox);
      setPendingAwb(null);
      setScanInput("");
      inputRef.current?.focus();
    } else {
      // Unrecognized code format
      soundFX.playError();
      const err = `Unrecognized code '${raw}'. AWB format must be 1 letter + digits (e.g. A12345678). Box QR format can be numeric (e.g. 123123) or prefixed (e.g. BX-123123, AHD123123).`;
      setScanError(err);
      UseToast(err, "error");
      setScanInput("");
    }
  };

  const handleCancelPendingAwb = () => {
    setPendingAwb(null);
    setScanError(null);
    setScanInput("");
    inputRef.current?.focus();
  };

  const handleRemovePair = (id: string) => {
    setStagedPairs((prev) => prev.filter((p) => p.id !== id));
    inputRef.current?.focus();
  };

  const handleClearAll = () => {
    setStagedPairs([]);
    setPendingAwb(null);
    setLastScannedBox(null);
    setScanError(null);
    inputRef.current?.focus();
  };

  const handleSaveLinks = async () => {
    if (stagedPairs.length === 0) return;

    if (pendingAwb !== null) {
      UseToast(`AWB #${pendingAwb} is waiting for its Box QR. Complete or cancel it before saving.`, "warning");
      return;
    }

    const result = await Swal.fire({
      title: "Confirm Linkage",
      text: `Link ${stagedPairs.length} box(es) to their respective AWBs?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0077B6",
      confirmButtonText: "Yes, Link Now",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setSaving(true);
      const res: any = await request({
        url: "/admin/box-tracker/link-awb",
        method: "POST",
        body: {
          data: stagedPairs.map((p) => ({ awbNo: p.awbNo, qrCode: p.qrCode })),
        },
      });

      if (res?.status === "SUCCESS" && res.data) {
        const { linkedCount = 0, failedCount = 0, results = [] } = res.data;
        setLastResults(results);

        if (failedCount === 0) {
          soundFX.playSuccess();
          UseToast(`Successfully linked all ${linkedCount} boxes!`, "success");
          setStagedPairs([]);
          setLastScannedBox(null);
        } else {
          soundFX.playError();
          UseToast(`Linked: ${linkedCount} succeeded, ${failedCount} failed. Check results below.`, "warning");
        }
      } else {
        soundFX.playError();
        UseToast(res?.message || "Failed to link boxes.", "error");
      }
    } catch (err: any) {
      soundFX.playError();
      UseToast(typeof err === "string" ? err : err?.message || "Error saving links.", "error");
    } finally {
      setSaving(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="space-y-4">
      {/* 1 Single Scanner Input Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
            <QrCodeIcon className="w-4 h-4 text-[#0077B6]" />
            <span>Barcode & QR Scanner</span>
          </label>

          {/* Dynamic Next Step Indicator */}
          {pendingAwb ? (
            <div className="flex items-center gap-2">
              <span className="text-xs bg-amber-100 text-amber-800 font-bold px-2.5 py-0.5 rounded-full border border-amber-300">
                AWB #{pendingAwb} locked &bull; Next: Scan Box QR
              </span>
              <button
                type="button"
                onClick={handleCancelPendingAwb}
                className="text-[11px] text-slate-400 hover:text-rose-600 font-medium"
                title="Cancel this AWB"
              >
                (Cancel)
              </button>
            </div>
          ) : (
            <span className="text-xs bg-sky-50 text-[#0077B6] font-bold px-2.5 py-0.5 rounded-full border border-sky-200">
              Next: Scan AWB (e.g. A12345678)
            </span>
          )}
        </div>

        {/* The 1 Input Form */}
        <form onSubmit={handleScanSubmit} className="relative">
          <input
            ref={inputRef}
            type="text"
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            placeholder={
              pendingAwb
                ? `AWB #${pendingAwb} locked. Now scan Box QR (e.g. 123123, BX-123123, AHD123123)...`
                : "Scan AWB Number (format: 1 letter + digits, e.g. A12345678)..."
            }
            className={`w-full pl-10 pr-20 py-3 text-sm font-mono font-bold rounded-xl border-2 transition-all outline-none ${
              pendingAwb
                ? "border-amber-400 bg-amber-50/20 focus:border-amber-500 focus:bg-white text-slate-900"
                : "border-slate-200 bg-slate-50 focus:border-[#0077B6] focus:bg-white text-slate-900"
            }`}
            autoFocus
          />
          <TruckIcon
            className={`w-5 h-5 absolute left-3 top-3.5 ${
              pendingAwb ? "text-amber-500" : "text-slate-400"
            }`}
          />
          <button
            type="submit"
            className="absolute right-2 top-2 px-3 py-1.5 bg-[#0077B6] hover:bg-[#023e8a] text-white text-xs font-bold rounded-lg transition-colors"
          >
            Enter
          </button>
        </form>

        {/* Format specifications guide */}
        <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap pt-0.5">
          <span className="flex items-center gap-1">
            <span className="font-semibold text-slate-700">AWB Format:</span>
            <span className="font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
              First alphabet + digits (e.g. A12345678)
            </span>
          </span>
          <span className="text-slate-300 sm:hidden">&bull;</span>
          <span className="flex items-center gap-1">
            <span className="font-semibold text-slate-700">Box QR Format:</span>
            <span className="font-mono bg-sky-50 text-[#0077B6] px-1.5 py-0.5 rounded border border-sky-100">
              Numeric (123123) or BX / Hub prefix (BX-123123, AHD123123)
            </span>
          </span>
        </div>

        {/* Scan Error Alert */}
        {scanError && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-xs font-medium flex items-center justify-between animate-fadeIn">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{scanError}</span>
            </div>
            <button
              type="button"
              onClick={() => setScanError(null)}
              className="text-rose-500 hover:text-rose-700"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Staged Links List Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Staged Box Links ({stagedPairs.length})
            </h3>
            {stagedPairs.length > 0 && (
              <span className="text-[11px] text-slate-400">Ready to save</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {stagedPairs.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-rose-600 hover:underline font-medium"
              >
                Clear All
              </button>
            )}

            <button
              type="button"
              disabled={saving || stagedPairs.length === 0}
              onClick={handleSaveLinks}
              className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : `Save & Link (${stagedPairs.length})`}
            </button>
          </div>
        </div>

        {stagedPairs.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-xs border border-dashed rounded-xl">
            No boxes staged yet. Scan an AWB, then scan its Box QR.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b text-[11px] font-bold uppercase text-slate-500">
                <tr>
                  <th className="py-2.5 px-3">#</th>
                  <th className="py-2.5 px-3">AWB Number</th>
                  <th className="py-2.5 px-3">Box QR Code</th>
                  <th className="py-2.5 px-3">Time</th>
                  <th className="py-2.5 px-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stagedPairs.map((pair, idx) => (
                  <tr key={pair.id} className="hover:bg-slate-50">
                    <td className="py-2.5 px-3 text-slate-400 font-mono">{idx + 1}</td>
                    <td className="py-2.5 px-3 font-mono font-bold text-slate-900">
                      {pair.awbNo}
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-[#0077B6]">
                      {pair.qrCode}
                    </td>
                    <td className="py-2.5 px-3 text-slate-400">{pair.time}</td>
                    <td className="py-2.5 px-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleRemovePair(pair.id)}
                        className="text-rose-500 hover:text-rose-700 p-1"
                        title="Remove"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Results of last saved batch if any */}
      {lastResults.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Last Save Result
          </h4>
          <div className="space-y-1.5">
            {lastResults.map((r, i) => (
              <div
                key={i}
                className={`p-2.5 rounded-lg border flex items-center justify-between text-xs ${
                  r.success
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-rose-50 border-rose-200 text-rose-900"
                }`}
              >
                <div className="flex items-center gap-2">
                  {r.success ? (
                    <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <ExclamationTriangleIcon className="w-4 h-4 text-rose-600" />
                  )}
                  <span className="font-mono font-bold">
                    AWB: {r.awbNo} &bull; Box: {r.qrCode}
                  </span>
                </div>
                <span className="text-[11px]">{r.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
